import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { UserPlus, Building2, DollarSign, FileCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { services } from '../../services';

interface QuickAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  onClick?: () => void;
  href?: string;
}

interface QuickActionsProps {
  className?: string;
}

export function QuickActions({ className }: QuickActionsProps) {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState(0);
  const [pendingKYC, setPendingKYC] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuickActionData();
  }, []);

  const fetchQuickActionData = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: {
          pendingUsers: number;
          pendingKYC: number;
        };
      }>('/admin/dashboard/quick-actions');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        setPendingUsers(response.data.data.pendingUsers || 0);
        setPendingKYC(response.data.data.pendingKYC || 0);
      } else if (response.status === 401) {
        console.error('Authentication required for quick actions');
      } else if (response.status === 403) {
        console.error('Admin access required for quick actions');
      }
    } catch (error: unknown) {
      interface AxiosError {
        response?: {
          status?: number;
          data?: unknown;
        };
      }
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for quick actions');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for quick actions');
      } else {
        console.error('Error fetching quick actions:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const actions: QuickAction[] = [
    {
      label: 'Approve Users',
      icon: UserPlus,
      description: loading ? 'Loading...' : `${pendingUsers} pending approvals`,
      href: '/users',
      onClick: () => navigate('/users'),
    },
    {
      label: 'Add Asset',
      icon: Building2,
      description: 'Tokenize new property',
      href: '/assets?openCreate=1',
      onClick: () => navigate('/assets?openCreate=1'),
    },
    {
      label: 'Distribute Yield',
      icon: DollarSign,
      description: 'Schedule payout',
      href: '/update-yields',
      onClick: () => navigate('/update-yields'),
    },
    {
      label: 'KYC Review',
      icon: FileCheck,
      description: loading ? 'Loading...' : `${pendingKYC} pending reviews`,
      href: '/users',
      onClick: () => navigate('/users'),
    },
  ];

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <h3 className='text-lg font-semibold text-foreground mb-4'>Quick Actions</h3>
      <div className='grid grid-cols-2 gap-3'>
        {actions.map((action) => (
          <Button
            key={action.label}
            variant='outline'
            className='flex-col h-auto py-4 px-4 gap-2 hover:border-primary/50 hover:bg-primary/5'
            onClick={action.onClick}
          >
            <action.icon className='w-6 h-6 text-primary' />
            <div className='text-center'>
              <p className='text-sm font-medium text-foreground'>{action.label}</p>
              <p className='text-xs text-muted-foreground'>{action.description}</p>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
}
